import { useState, type MouseEvent } from 'react';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { AdminGuideListItem } from '@/features/admin/api/admin.api';
import { hasPublicCard } from './guideDirectory.utils';

interface GuideActionsMenuProps {
  guide: AdminGuideListItem;
  canDelete: boolean;
  onView: () => void;
  onOpenIdentityCard: () => void;
  onCopy: (value: string, label: string) => void;
  onDelete: () => void;
}

export function GuideActionsMenu({
  guide,
  canDelete,
  onView,
  onOpenIdentityCard,
  onCopy,
  onDelete,
}: GuideActionsMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  // Rows are clickable; the menu must never also trigger the row.
  const stop = (event: MouseEvent) => event.stopPropagation();
  const run = (action: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    setAnchor(null);
    action();
  };

  const { identity, user } = guide;

  return (
    <>
      <Tooltip title="More actions">
        <IconButton
          size="small"
          aria-label="More actions"
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          onClick={(event) => {
            event.stopPropagation();
            setAnchor(event.currentTarget);
          }}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'var(--radius-md)', width: 32, height: 32 }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        // The menu is portalled, but React still bubbles its clicks (backdrop
        // included) to the clickable row behind it.
        onClick={stop}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 220,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'var(--shadow-lg)',
              borderRadius: 'var(--radius-xl)',
              '& .MuiMenuItem-root': { fontSize: '0.875rem', py: 1 },
              '& .MuiListItemIcon-root': { minWidth: 32 },
            },
          },
        }}
      >
        <MenuItem onClick={run(onView)}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View profile" />
        </MenuItem>
        {identity && (
          <MenuItem onClick={run(onOpenIdentityCard)}>
            <ListItemIcon>
              <BadgeOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit identity card" />
          </MenuItem>
        )}
        {identity && hasPublicCard(guide) && (
          <MenuItem
            component="a"
            href={identity.identityUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setAnchor(null)}
          >
            <ListItemIcon>
              <OpenInNewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Open public card" />
          </MenuItem>
        )}

        {(identity || user?.email || user?.phone) && <Divider sx={{ my: 0.5 }} />}
        {identity && (
          <MenuItem onClick={run(() => onCopy(identity.guideId, 'Guide ID'))}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy Guide ID" />
          </MenuItem>
        )}
        {user?.email && (
          <MenuItem onClick={run(() => onCopy(user.email!, 'Email'))}>
            <ListItemIcon>
              <AlternateEmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy email" />
          </MenuItem>
        )}
        {user?.phone && (
          <MenuItem onClick={run(() => onCopy(user.phone!, 'Phone'))}>
            <ListItemIcon>
              <PhoneOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Copy phone" />
          </MenuItem>
        )}

        {canDelete && <Divider sx={{ my: 0.5 }} />}
        {canDelete && (
          <MenuItem onClick={run(onDelete)} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Delete guide" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
